import {useEffect, useState} from "react";
import styled from "styled-components";
import {useLocation} from "react-router-dom";
import {ReactComponent as InfoIcon} from '../../resources/icons/info.svg';
import { darken } from 'polished';
import showHelpPopup from "../../popups/showHelpPopup";
import {useMenuDataContext} from "../../contexts/MenuDataContext";
import Api from "../../util/Api";
import {checkRequiredFeatureFlags} from "../../util/Helpers";
import {FeatureFlags} from "../../constants/featureFlags";
import {HelpTypes} from "../../constants/enums";
import { primaryPurple, secondaryPinkMedium } from "../../Theme";

const StyledFloatingHelpButton = styled.button`
    position: fixed;
    bottom: 20px;
    right: 20px;
    padding: 12px;
    border: none;
    background-color: ${secondaryPinkMedium};
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    cursor: pointer;
    transition: background-color 0.15s ease, transform 0.15s ease;

    &:hover {
        background-color: ${() => darken(0.05, secondaryPinkMedium)};
        transform: scale(1.05);
    }

    @media screen and (max-width: 768px) {
        bottom: ${p => p.$isDetailPage ? "128px" : "70px"};
        right: 8px;
    }
`;

const StyledInfoIcon = styled(InfoIcon)`
    display: block;
    width: 40px;
    height: 40px;
    color: ${primaryPurple};

    @media screen and (max-width: 768px) {
        width: 28px;
        height: 28px;
    }
`;

export const FloatingHelpButton = () => {
    const {pathname} = useLocation();
    const {featureFlags} = useMenuDataContext();
    const [helpPages, setHelpPages] = useState([]);

    useEffect(() => {
        Api.get('/more', (response) => {
            setHelpPages(Array.isArray(response?.data) ? response.data : []);
        });
    }, []);

    const helpType = getHelpTypeForPathname(pathname);
    const helpPage = helpPages.find(page => page.type === helpType);

    const handleClick = () => {
        showHelpPopup(helpPage?.title, helpPage?.subtitle, helpPage?.items);
    }

    if (!helpType || !helpPage || isPathShowingNoAccess(pathname, featureFlags)) return null;

    return (
        <StyledFloatingHelpButton type="button" onClick={handleClick} $isDetailPage={isDetailPage(pathname)}>
            <StyledInfoIcon />
        </StyledFloatingHelpButton>
    );
};

function getHelpTypeForPathname(pathname) {
    if (pathname.startsWith("/mentor/ef-voortgang")) {
        return HelpTypes.MENTOR_PROGRESS;
    }
    if (pathname.startsWith("/mentor/planagenda") || pathname.startsWith("/dashboard")) {
        return HelpTypes.MENTOR_PUPILS;
    }
    if (pathname === "/" || pathname.startsWith("/ef-voortgang") || pathname.startsWith("/plan-van-aanpak")) {
        return HelpTypes.PERIODS;
    }
    if (pathname.startsWith("/executieve-functies")) {
        return HelpTypes.EXECUTIVE_FUNCTIONS;
    }
    if (pathname.startsWith("/toetsweek") || pathname.startsWith("/toets")) {
        return HelpTypes.EXAMS;
    }
    if (pathname.startsWith("/planagenda") || pathname.startsWith("/huiswerk")) {
        return HelpTypes.SCHEDULE;
    }
    return null;
}

function isPathShowingNoAccess(pathname, featureFlags) {
    if (checkRequiredFeatureFlags(featureFlags, FeatureFlags.FEATURE_SCHEDULE)) {
        return false;
    }
    if (pathname === "/planagenda") {
        return true;
    }
    if (pathname === "/toetsweek") {
        return true;
    }
    if (pathname === "/dashboard" || pathname === "/mentor/planagenda") {
        return true;
    }
    return false;
}

function isDetailPage(pathname) {
    const pathParts = pathname.split("/").filter(Boolean);

    if (pathname.startsWith("/mentor/ef-voortgang")) {
        if (pathParts.length >= 3) return true;
    } else if (pathname.startsWith("/ef-voortgang")) {
        if (pathParts.length >= 2) return true;
    }

    return false;
}